// @ts-nocheck
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useMutation } from "@apollo/react-hooks";
import {
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogContent,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import useStyles from "./new-request-dialog.style";
import CloseIcon from "@iconify/icons-mdi/close";
import { InlineIcon } from "@iconify/react";
import "date-fns";
import { useMyHrContractsByEmployee } from "my-hr/hooks";
import {
  MyLeaveEffectiveWorkDaysBlock,
  MyLeaveLeaveCards,
} from "my-leave/components";
import { CoverageErrorMessage } from "./coverage-error-message";
import { validateRepeatedHOCoverage } from "./validate-repeated-ho-coverage";
import { MY_LEAVE_CREATE_REQUEST, MY_LEAVE_CREATE_REPEATED_HO_REQUEST } from "my-leave/graphql";
import {
  useMyLeaveSummaryChartData,
  useMyLeaveTypesByContract,
  useMyLeaveWorkDays,
  useReqestLeavesData,
} from "my-leave/hooks";
import { OcCard, OcDatePicker, OcFormSwitch, OcTimeDateCounter } from "oc/components";
import {
  useOcContractCalendar,
  useOcCurrentUserState,
  useOcIsMobile,
  useOcSnackbar,
  useOcProperty
} from "oc/hooks";
import { getDateFormatByLocale } from "oc/utils";
import { isValidDate } from "oc/utils/is-valid-date";

export const MyLeaveNewRequestDialog = ({
  showDialog,
  onShowDialog,
  onCoverage,
  assignEmployeeCode,
  assignContractCode,
  leaveRequests,
}) => {
  let now = new Date();
  const endOfCurrentYear = moment(now).endOf('year').toDate();
  const { t, i18n } = useTranslation();
  const isMobile = useOcIsMobile();
  const classes = useStyles({ isMobile });
  const { setOpenMessage } = useOcSnackbar();

  const [saveButtonDisabled, setSaveButtonDisabled] = useState(false);
  const [leaveType, setLeaveType] = useState("S");
  const [leaveStart, setLeaveStart] = useState(now);
  const [leaveEnd, setLeaveEnd] = useState(now);
  const [message, setMessage] = useState("");
  const [leaveStartError, setLeaveStartError] = useState(false);
  const [leaveEndError, setLeaveEndError] = useState(false);
  const [leaveTypeError, setLeaveTypeError] = useState(false);
  const [coverageError, setCoverageError] = useState(false);
  const [coverageErrorRequests, setCoverageErrorRequests] = useState<Array<{ type: string, startDate: Date, endDate: Date }>>([]);
  const [workDaysError, setWorkDaysError] = useState(false);
  const [moreDaysError, setMoreDaysError] = useState(false);
  const [currentDate, setCurrentDate] = useState(now);
  const [expiredContractError, setExpiredContractError] = useState(false);
  const [nextYearBlockedError, setNextYearBlockedError] = useState(false);
  const [invalidYearError, setInvalidYearError] = useState(false);

  // Repeated HomeOffice specific states
  const [isRepeatedHOMode, setIsRepeatedHOMode] = useState(false);
  const [selectedWeekDays, setSelectedWeekDays] = useState<number[]>([]);
  const [repeatedStartDate, setRepeatedStartDate] = useState(now);
  const [repeatedEndDate, setRepeatedEndDate] = useState(moment(now).endOf('year').toDate());
  const [weekDaysError, setWeekDaysError] = useState(false);
  const [repeatedDateError, setRepeatedDateError] = useState(false);

  let { contractCode, employeeCode } = useOcCurrentUserState();

  if (assignEmployeeCode) {
    employeeCode = assignEmployeeCode;
  }

  if (assignContractCode) {
    contractCode = assignContractCode;
  }

  const isAssigner = assignEmployeeCode || assignContractCode ? true : false;

  const {
    data: blockNextYearLeaveCreationRaw,
    loading: blockNextYearLeaveCreationLoading,
  } = useOcProperty({
    resource: "myLeave",
    key: "blockNextYearLeaveCreation",
  });

  const blockNextYearLeaveCreation = blockNextYearLeaveCreationRaw === "true";

  const { data: contractsData } = useMyHrContractsByEmployee({
    employee: employeeCode,
  });

  const expiredContractDate = contractsData.map((contract) => {
    if (contract.validTo !== null) {
      const validToDate = new Date(contract.validTo);
      validToDate.setDate(validToDate.getDate() + 1);
      return validToDate.toISOString();
    }
    return null;
  });

  const leaveStartYear = moment(leaveStart)?.format("YYYY");
  const leaveEndYear = moment(leaveEnd)?.format("YYYY");
  const currentYear = new Date().getFullYear().toString();

  const selectedYear = blockNextYearLeaveCreation
    ? currentYear
    : leaveStart?.getFullYear().toString();

  let nextYear = null;

  if (leaveStartYear !== leaveEndYear && leaveType === "S") {
    if (!blockNextYearLeaveCreation) {
      nextYear = leaveEndYear;
    }
  }

  const { requestedLeaveDays } = useReqestLeavesData({
    selectedYear,
    contractCode,
  });

  const { data: leaveSummary, error: leaveSummaryError } = useMyLeaveSummaryChartData({
    year: selectedYear,
    requestedLeaveDays,
    contract: contractCode,
  });

  let thisYear = new Date().getFullYear().toString();

  const { requestedLeaveDaysNextYear } = useReqestLeavesData({
    nextYear,
    contractCode,
  });

  const { data: leaveSummaryNextYear } = useMyLeaveSummaryChartData({
    year: nextYear,
    requestedLeaveDaysNextYear,
    contract: contractCode,
  });

  const getStartOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  const handleCurrentDate = (date) => {
    if (moment(date).format("YYYY") !== moment(currentDate).format("YYYY")) {
      setCurrentDate(moment(date).endOf("day"));
    }
  };

  const startOfYear = moment(currentDate).startOf("year");
  const endOfYear = moment(currentDate).endOf("year");

  const { contractCalendar } = useOcContractCalendar({
    contract: contractCode,
    startDate: startOfYear,
    endDate: endOfYear,
  });

  const {
    workDays,
    effectiveWorkDays,
    effectiveWorkDaysByDays,
    currentYearWorkDays,
    currentYearEffectiveWorkDays,
    nextYearWorkDays,
    nextYearEffectiveWorkDays,
    workDaysloading,
  } = useMyLeaveWorkDays({
    contract: contractCode,
    startDate: leaveStart,
    endDate: leaveEnd,
  });

  // Repeated HomeOffice workDays calculation
  const {
    workDaysloading: repeatedWorkDaysLoading,
  } = useMyLeaveWorkDays({
    contract: contractCode,
    startDate: repeatedStartDate,
    endDate: repeatedEndDate,
  });

  // Calculate actual workdays based on selected weekdays
  const repeatedWorkDays = useMemo(() => {
    if (!isRepeatedHOMode || selectedWeekDays.length === 0) {
      return 0;
    }

    // Check if contractCalendar is available
    if (!contractCalendar) {
      return 0;
    }

    let count = 0;
    const current = moment(repeatedStartDate).clone();
    const end = moment(repeatedEndDate);

    // Count occurrences of selected weekdays in the date range, excluding holidays
    while (current.isSameOrBefore(end, 'day')) {
      const dayOfWeek = current.day(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday

      // Check if this day is in selectedWeekDays
      if (selectedWeekDays.includes(dayOfWeek)) {
        // Check if it's a workday using contractCalendar
        const dayOfYear = current.dayOfYear();
        const dayType = contractCalendar[dayOfYear - 1];

        // Only count actual workdays (represented by "." in contractCalendar)
        // P = Pihenőnap (rest day/weekend/holiday) - DO NOT COUNT
        // F = Fizetett szabadság (paid holiday) - DO NOT COUNT
        // . = Munkanap (workday) - COUNT THIS ONLY
        if (dayType === '.') {
          count++;
        }
      }

      current.add(1, 'day');
    }

    return count;
  }, [isRepeatedHOMode, selectedWeekDays, repeatedStartDate, repeatedEndDate, contractCalendar]);

  // Check if leave request spans multiple months
  const isMonthSplit = useMemo(() => {
    if (!leaveStart || !leaveEnd) return false;
    return moment(leaveStart).format("YYYY-MM") !== moment(leaveEnd).format("YYYY-MM");
  }, [leaveStart, leaveEnd]);

  const { leaveTypesByContract } = useMyLeaveTypesByContract({
    contract: contractCode,
    startDate: leaveStart,
    endDate: leaveEnd,
  });

  const [createRequest] = useMutation(MY_LEAVE_CREATE_REQUEST, {
    onCompleted: () => {
      setSaveButtonDisabled(false);
      setOpenMessage({
        type: "success",
        message: t("MY_LEAVE_SAVE_SUCCEEDED"),
      });
    },
    onError: (error) => {
      setSaveButtonDisabled(false);
      const errorMessage =
        error?.graphQLErrors?.[0]?.extensions?.exception?.data?.error?.type ||
        t("MY_LEAVE_SAVE_FAILED");
      setOpenMessage({
        type: "error",
        message: t(errorMessage),
      });
    },
  });

  const [createRepeatedHORequest] = useMutation(MY_LEAVE_CREATE_REPEATED_HO_REQUEST, {
    onCompleted: (data) => {
      setSaveButtonDisabled(false);
      setOpenMessage({
        type: "success",
        message: t("MY_LEAVE_REPEATED_HO_SAVE_SUCCEEDED", { count: data.createRepeatedHOLeaveRequest.count }),
      });
    },
    onError: (error) => {
      setSaveButtonDisabled(false);
      const errorMessage =
        error?.graphQLErrors?.[0]?.extensions?.exception?.data?.error?.type ||
        t("MY_LEAVE_SAVE_FAILED");
      setOpenMessage({
        type: "error",
        message: t(errorMessage),
      });
    },
  });

  const handleLeaveType = (e) => {
    const newType = e.target.value;
    setLeaveType(newType);
    setLeaveTypeError(false);

    // Reset repeated HO mode if type is not O
    if (newType !== "O") {
      setIsRepeatedHOMode(false);
      setSelectedWeekDays([]);
      setWeekDaysError(false);
      setRepeatedDateError(false);
    }
  };

  const handleLeaveStart = (date) => {
    if (!isValidDate(date)) return;
    const localTime = moment(date).format("HHmmSS");
    if (localTime === "000000") {
      date = moment(date).add(12, "hours").toDate();
    }

    const referenceDate = isAssigner
      ? moment().subtract(1, "month").toDate()
      : new Date();
    const startOfMonth = getStartOfMonth(referenceDate);
    if (date < startOfMonth) {
      setLeaveStartError(true);
      setLeaveEndError(true);
    } else {
      setLeaveStartError(false);
      setLeaveEndError(false);
    }
    setLeaveStart(date);
    setLeaveEnd(date);
    setWorkDaysError(false);
  };

  const handleLeaveEnd = (date) => {
    if (!isValidDate(date)) return;

    const localTime = moment(date).format("HHmmSS");
    if (localTime === "000000") {
      date = moment(date).add(12, "hours").toDate();
    }

    let start = new Date(leaveStart);
    let end = new Date(date);
    if (start > end) {
      setLeaveEndError(true);
    } else {
      setLeaveEndError(false);
    }
    setLeaveEnd(date);
    setWorkDaysError(false);
  };

  const handleMessage = (e) => {
    setMessage(e.target.value);
  };

  const [doCoverage, setDoCoverage] = React.useState(true);

  const handleRepeatedStartDate = (date) => {
    if (!isValidDate(date)) return;
    const localTime = moment(date).format("HHmmSS");
    if (localTime === "000000") {
      date = moment(date).add(12, "hours").toDate();
    }

    const referenceDate = isAssigner
      ? moment().subtract(1, "month").toDate()
      : new Date();
    const startOfMonth = getStartOfMonth(referenceDate);

    // Validate: not in the past AND not beyond current year
    if (date < startOfMonth || moment(date).isAfter(endOfCurrentYear, 'day')) {
      setRepeatedDateError(true);
    } else {
      setRepeatedDateError(false);
    }
    setRepeatedStartDate(date);
  };

  const handleRepeatedEndDate = (date) => {
    if (!isValidDate(date)) return;

    const localTime = moment(date).format("HHmmSS");
    if (localTime === "000000") {
      date = moment(date).add(12, "hours").toDate();
    }

    // Validate: after start date AND not beyond current year
    if ((repeatedStartDate && date <= repeatedStartDate) || moment(date).isAfter(endOfCurrentYear, 'day')) {
      setRepeatedDateError(true);
    } else {
      setRepeatedDateError(false);
    }
    setRepeatedEndDate(date);
  };

  const handleWeekDayToggle = (day: number) => {
    setSelectedWeekDays((prev) => {
      if (prev.includes(day)) {
        return prev.filter((d) => d !== day);
      } else {
        return [...prev, day];
      }
    });
    setWeekDaysError(false);
  };

  const handleClear = () => {
    setLeaveType("S");
    setLeaveStart(now);
    setLeaveEnd(now);
    setMessage("");
    setLeaveStartError(false);
    setLeaveEndError(false);
    setLeaveTypeError(false);
    setCoverageError(false);
    setCoverageErrorRequests([]);
    setWorkDaysError(false);
    setMoreDaysError(false);
    setExpiredContractError(false);
    setNextYearBlockedError(false);
    setInvalidYearError(false);
    setDoCoverage(true);

    // Clear repeated HO states
    setIsRepeatedHOMode(false);
    setSelectedWeekDays([]);
    setRepeatedStartDate(now);
    setRepeatedEndDate(moment(now).endOf('year').toDate());
    setWeekDaysError(false);
    setRepeatedDateError(false);
  };

  const handleCancel = () => {
    onShowDialog(false);
    setTimeout(() => {
      handleClear();
    }, 500);
  };

  const handleSave = async () => {
    setSaveButtonDisabled(true);

    // Repeated HomeOffice mode
    if (leaveType === "O" && isRepeatedHOMode) {
      // Validation: at least one weekday selected
      if (selectedWeekDays.length === 0) {
        setWeekDaysError(true);
        setSaveButtonDisabled(false);
        return;
      }

      // Validation: date range
      if (moment(repeatedStartDate).isSameOrAfter(repeatedEndDate)) {
        setRepeatedDateError(true);
        setSaveButtonDisabled(false);
        return;
      }

      // Validation: at least one workday in the selected range
      if (repeatedWorkDays === 0) {
        setWorkDaysError(true);
        setSaveButtonDisabled(false);
        return;
      }

      // Validation: start date not in the past
      const repeatedStartMonth = moment(repeatedStartDate).format("YYYY-MM");
      const referenceDate = isAssigner
        ? moment().subtract(1, "month").format("YYYY-MM")
        : moment().format("YYYY-MM");
      if (repeatedStartMonth < referenceDate) {
        setRepeatedDateError(true);
        setSaveButtonDisabled(false);
        return;
      }

      // Validation: dates must not exceed current year
      if (moment(repeatedStartDate).isAfter(endOfCurrentYear, 'day') || moment(repeatedEndDate).isAfter(endOfCurrentYear, 'day')) {
        setRepeatedDateError(true);
        setSaveButtonDisabled(false);
        return;
      }

      // Validation: coverage error (overlap with existing requests)
      if (coverageError) {
        setSaveButtonDisabled(false);
        return;
      }

      let variables = {
        contract: contractCode,
        type: leaveType,
        weekDays: selectedWeekDays,
        repeatedStartDate,
        repeatedEndDate,
        locale: i18n.language,
        isAssigner: isAssigner,
      };
      if (message?.trim().length > 0) {
        variables.message = message;
      }

      await createRepeatedHORequest({
        variables,
      });
      onShowDialog(false);
      handleClear();
      return;
    }

    // Standard single request mode
    const leaveStartMonth = moment(leaveStart).format("YYYY-MM");
    const referenceDate = isAssigner
      ? moment().subtract(1, "month").format("YYYY-MM")
      : moment().format("YYYY-MM");
    if (leaveStartMonth < referenceDate) {
      setLeaveStartError(true);
      setSaveButtonDisabled(false);
      return;
    }

    if (
      leaveStartError ||
      leaveEndError ||
      leaveTypeError ||
      coverageError ||
      moreDaysError ||
      expiredContractError ||
      nextYearBlockedError ||
      invalidYearError
    ) {
      setSaveButtonDisabled(false);
      return;
    }

    if (workDays === 0) {
      setWorkDaysError(true);
      setSaveButtonDisabled(false);
      return;
    }
    if (!leaveType) {
      setSaveButtonDisabled(false);
      setLeaveTypeError(true);
      return;
    }

    let variables = {
      contract: contractCode,
      startDate: leaveStart,
      endDate: leaveEnd,
      workDays: workDays,
      effectiveWorkDays: effectiveWorkDays,
      type: leaveType,
      locale: i18n.language,
      role: "user",
      isAssigner: isAssigner,
    };
    if (message?.trim().length > 0) {
      variables.message = message;
    }

    setMoreDaysError(false);
    setCoverageError(false);
    setDoCoverage(false);
    await createRequest({
      variables,
    });
    onShowDialog(false);
    handleClear();
  };

  useEffect(() => {
    if (leaveSummaryError) {
      const errorMessage = leaveSummaryError?.message;

      // Check error message for year validation errors
      const isYearError =
        errorMessage?.includes("MY_LEAVE_INVALID_YEAR_ERROR") ||
        errorMessage?.includes("Wrong year parameter format");

      setInvalidYearError(isYearError);
    } else {
      setInvalidYearError(false);
    }

    if (leaveStart && leaveEnd && leaveType && doCoverage) {
      setCoverageError(
        onCoverage(
          {
            startDate: leaveStart,
            endDate: leaveEnd,
            leaveType,
          },
          leaveRequests,
        ),
      );
    }

    if (leaveType) {
      let requestable;
      let requestableNextYear;
      switch (leaveType) {
        case "S":
          requestable = leaveSummary?.availableLeaveDays;
          requestableNextYear = leaveSummaryNextYear?.availableLeaveDays;
          break;
        case "Q":
          requestable = leaveSummary?.availableCovidPlus10LeaveDays;
          break;
        case "T":
          requestable = leaveSummary?.availableStudyLeaveDays;
          break;
        case "D":
          requestable = leaveSummary?.availableESZJTV19LeaveDays;
          break;
        case "V":
          requestable = leaveSummary?.availableEmergencyLeaveDays;
          break;

        default:
          break;
      }

      let error = false;
      if (nextYear) {
        error =
          requestable - currentYearWorkDays < 0 ||
          requestableNextYear - nextYearWorkDays < 0;
      } else {
        error = requestable?.toFixed(3) - effectiveWorkDays < 0;
      }

      if (error && showDialog && !saveButtonDisabled) {
        setMoreDaysError(true);
      } else {
        setMoreDaysError(false);
      }

      if (
        leaveStart > new Date(expiredContractDate) ||
        leaveEnd > new Date(expiredContractDate)
      ) {
        setExpiredContractError(true);
      } else {
        setExpiredContractError(false);
      }
    }

    // Repeated HO coverage validation
    if (isRepeatedHOMode && leaveType === "O" && selectedWeekDays.length > 0 && repeatedStartDate && repeatedEndDate && showDialog && !saveButtonDisabled) {
      const { hasOverlap, overlappingRequests } = validateRepeatedHOCoverage(
        selectedWeekDays,
        repeatedStartDate,
        repeatedEndDate,
        leaveRequests
      );

      // Only update state if it actually changed (prevent infinite loop)
      setCoverageError((prev) => {
        if (prev !== hasOverlap) return hasOverlap;
        return prev;
      });

      setCoverageErrorRequests((prev) => {
        // Compare arrays by content, not reference
        if (overlappingRequests.length !== prev.length) return overlappingRequests;

        const isSame = overlappingRequests.every((req, index) => {
          const prevReq = prev[index];
          return (
            prevReq &&
            req.type === prevReq.type &&
            req.startDate.getTime() === prevReq.startDate.getTime() &&
            req.endDate.getTime() === prevReq.endDate.getTime()
          );
        });

        return isSame ? prev : overlappingRequests;
      });
    } else if (isRepeatedHOMode) {
      // Clear repeated HO coverage errors when not all conditions met
      setCoverageError((prev) => prev ? false : prev);
      setCoverageErrorRequests((prev) => prev.length > 0 ? [] : prev);
    }

    const isNextYearOrLater =
      parseInt(leaveStartYear) > parseInt(currentYear) ||
      parseInt(leaveEndYear) > parseInt(currentYear);

    if (!blockNextYearLeaveCreationLoading) {
      if (blockNextYearLeaveCreationRaw === "true" && isNextYearOrLater && leaveType === "S") {
        setNextYearBlockedError(true);
        if (parseInt(leaveStartYear) > parseInt(currentYear)) {
          setLeaveStartError(true);
        }
        if (parseInt(leaveEndYear) > parseInt(currentYear)) {
          setLeaveEndError(true);
        }
      } else {
        setNextYearBlockedError(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    leaveStart,
    leaveEnd,
    leaveType,
    leaveTypesByContract,
    onCoverage,
    requestedLeaveDays,
    t,
    leaveSummary,
    leaveSummaryNextYear,
    leaveSummaryError,
    workDays,
    currentYearWorkDays,
    nextYearWorkDays,
    doCoverage,
    nextYear,
    effectiveWorkDays,
    moreDaysError,
    saveButtonDisabled,
    expiredContractDate,
    blockNextYearLeaveCreationRaw,
    blockNextYearLeaveCreationLoading,
    leaveStartYear,
    leaveEndYear,
    currentYear,
    isRepeatedHOMode,
    selectedWeekDays,
    repeatedStartDate,
    repeatedEndDate,
    showDialog,
  ]);

  const selectValues =
    leaveTypesByContract.length === 0
      ? [
        {
          code: "S",
          name: "Szabadság",
          type: "leave",
        },
      ]
      : leaveTypesByContract.filter(
        (item) => item?.code !== "6" && item?.code !== "7",
      );

  const leaveTypelist =
    leaveType === "Q" ||
    leaveType === "S" ||
    leaveType === "D" ||
    leaveType === "T" ||
    leaveType === "V";

  return (
    <Dialog
      fullScreen={isMobile ? true : false}
      aria-labelledby="new-request-dialog-title"
      open={showDialog}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          return false;
        }
      }}
      disableEscapeKeyDown
      scroll="body"
      PaperProps={{
        style: { minWidth: isMobile ? "100%" : "442px" },
      }}
    >
      <Box height="65px">
        <OcCard
          label={
            isAssigner ? t("MY_LEAVE_NEW_LEAVE") : t("MY_LEAVE_NEW_REQUEST")
          }
        />
        {isMobile && (
          <Box position="absolute" right="16px" top="16px">
            <InlineIcon
              icon={CloseIcon}
              width="24px"
              onClick={handleCancel}
              style={{ cursor: "pointer" }}
            />
          </Box>
        )}
      </Box>
      <DialogContent className={classes.content}>
        {!isAssigner && (
          <>
            <Box height="25px" />
            <FormControl variant="outlined" className={classes.formControl}>
              <InputLabel
                id="type-select-label-id"
                className={classes.selectInput}
              >
                <span style={{ paddingLeft: 8, paddingRight: 8 }}>
                  {t("OC_TYPE")}
                </span>
              </InputLabel>
              <Select
                labelId="type-select-label-id"
                id="leaveType"
                name="leaveType"
                value={leaveType ? leaveType : "S"}
                onChange={handleLeaveType}
                error={leaveTypeError}
                style={{
                  fontSize: 16,
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                }}
                autoWidth
                fullWidth
                className={classes.selectControl}
                inputProps={{
                  classes: {
                    root: classes.selectInput,
                  },
                }}
              >
                {selectValues?.map((event, idx) => (
                  <MenuItem
                    key={`select-item-${idx}`}
                    value={event?.code}
                    data-value={event?.code}
                    style={{ width: isMobile ? "auto" : 400 }}
                  >
                    <div
                      style={{
                        width: 360,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {t(`MY_LEAVE_${event?.code}`)}
                    </div>
                  </MenuItem>
                ))}
              </Select>
              {leaveTypeError && (
                <FormHelperText style={{ color: "#cc0033", minHeight: 32 }}>
                  {leaveTypeError && "A kérelem típusának a megadása kötelező!"}
                </FormHelperText>
              )}
            </FormControl>
            <Box height="16px" />
          </>
        )}
        {leaveType === "O" && !isAssigner && (
          <>
            <Box display="flex" flexDirection="row" alignItems="center">
              <OcFormSwitch
                id="repeated-ho-mode-switch"
                checked={isRepeatedHOMode}
                inputProps={{ "aria-label": "repeated-ho-mode" }}
                onChange={(e) => {
                  setIsRepeatedHOMode(e.target.checked);
                  if (!e.target.checked) {
                    setSelectedWeekDays([]);
                    setWeekDaysError(false);
                    setRepeatedDateError(false);
                  }
                }}
              />
              <Box width="16px" />
              <Typography variant="body1">
                {t("MY_LEAVE_ENABLE_WEEKLY_REPEAT")}
              </Typography>
            </Box>

            {isRepeatedHOMode && (
              <>
                <Box height="16px" />
                <Box>
                  <Box
                    style={{
                      color: "#666",
                      fontSize: "12px",
                      padding: "0px 8px 8px 8px",
                    }}
                  >
                    {t("MY_LEAVE_SELECT_WEEKDAYS")}
                  </Box>
                  {isMobile ? (
                    <ButtonGroup orientation="vertical" fullWidth>
                      {[
                        { value: 1, label: t("OC_MONDAY") },
                        { value: 2, label: t("OC_TUESDAY") },
                        { value: 3, label: t("OC_WEDNESDAY") },
                        { value: 4, label: t("OC_THURSDAY") },
                        { value: 5, label: t("OC_FRIDAY") },
                        { value: 6, label: t("OC_SATURDAY") },
                        { value: 0, label: t("OC_SUNDAY") },
                      ].map((day) => {
                        const isSelected = selectedWeekDays.includes(day.value);
                        return (
                          <Button
                            key={day.value}
                            variant={isSelected ? "contained" : "outlined"}
                            color={isSelected ? "primary" : "inherit"}
                            disableElevation
                            onClick={() => handleWeekDayToggle(day.value)}
                          >
                            {day.label}
                          </Button>
                        );
                      })}
                    </ButtonGroup>
                  ) : (
                    <Box>
                      <ButtonGroup>
                        {[
                          { value: 1, label: t("OC_MONDAY") },
                          { value: 2, label: t("OC_TUESDAY") },
                          { value: 3, label: t("OC_WEDNESDAY") },
                          { value: 4, label: t("OC_THURSDAY") },
                        ].map((day) => {
                          const isSelected = selectedWeekDays.includes(day.value);
                          return (
                            <Button
                              key={day.value}
                              variant={isSelected ? "contained" : "outlined"}
                              color={isSelected ? "primary" : "inherit"}
                              disableElevation
                              onClick={() => handleWeekDayToggle(day.value)}
                            >
                              {day.label}
                            </Button>
                          );
                        })}
                      </ButtonGroup>
                      <Box height="8px" />
                      <ButtonGroup>
                        {[
                          { value: 5, label: t("OC_FRIDAY") },
                          { value: 6, label: t("OC_SATURDAY") },
                          { value: 0, label: t("OC_SUNDAY") },
                        ].map((day) => {
                          const isSelected = selectedWeekDays.includes(day.value);
                          return (
                            <Button
                              key={day.value}
                              variant={isSelected ? "contained" : "outlined"}
                              color={isSelected ? "primary" : "inherit"}
                              disableElevation
                              onClick={() => handleWeekDayToggle(day.value)}
                            >
                              {day.label}
                            </Button>
                          );
                        })}
                      </ButtonGroup>
                    </Box>
                  )}
                </Box>
              </>
            )}
          </>
        )}
        <Box paddingTop={leaveType !== "S" || nextYear ? "0px" : "16px"}>
          {nextYear && <Typography paddingBottom="4px">{thisYear}</Typography>}
          <MyLeaveLeaveCards
            contractCode={contractCode}
            selectedYear={thisYear}
            showFilter={[...leaveType]}
            padding=""
          />
          {nextYear && (
            <Box>
              <Typography padding="46px 0px 4px">{nextYear}</Typography>
              <MyLeaveLeaveCards
                contractCode={contractCode}
                selectedYear={nextYear}
                showFilter={[...leaveType]}
                padding=""
              />
            </Box>
          )}
        </Box>
        <form noValidate>
          <div className={classes.centerContainer}>
            {/* Conditional rendering: Repeated HO mode vs Standard mode */}
            {isRepeatedHOMode && leaveType === "O" && !isAssigner ? (
              <>
                <div className={classes.pickerContainer}>
                  <OcDatePicker
                    error={repeatedDateError}
                    helperText={repeatedDateError && t("OC_NOT_SELECTABLE")}
                    id="repeatedStartDate"
                    label={t("MY_LEAVE_REPEATED_START_DATE")}
                    minDate={new Date("2020-09-01")}
                    maxDate={endOfCurrentYear}
                    onChange={handleRepeatedStartDate}
                    value={repeatedStartDate}
                    format={getDateFormatByLocale(i18n.language)}
                    contractCalendar={contractCalendar}
                  />
                  <OcDatePicker
                    error={repeatedDateError}
                    helperText={repeatedDateError && t("OC_NOT_SELECTABLE")}
                    id="repeatedEndDate"
                    label={t("MY_LEAVE_REPEATED_END_DATE")}
                    minDate={repeatedStartDate}
                    maxDate={endOfCurrentYear}
                    onChange={handleRepeatedEndDate}
                    value={repeatedEndDate}
                    format={getDateFormatByLocale(i18n.language)}
                    contractCalendar={contractCalendar}
                  />
                </div>
                {isMobile && <Box width="26px" />}
                <OcTimeDateCounter
                  leaveStart={repeatedStartDate}
                  leaveEnd={repeatedEndDate}
                  time={repeatedWorkDays}
                  timeloading={repeatedWorkDaysLoading}
                  cssClasses="leave"
                  counterLabel={t("MY_LEAVE_COUNT_OF_WORKDAYS")}
                  isMobile={isMobile}
                />
              </>
            ) : (
              <>
                <div className={classes.pickerContainer}>
                  <OcDatePicker
                    error={leaveStartError}
                    helperText={leaveStartError && t("OC_NOT_SELECTABLE")}
                    id="leaveStart"
                    label={
                      isAssigner ? t("OC_START_TIME") : t("MY_LEAVE_LEAVE_START")
                    }
                    minDate={new Date("2020-09-01")}
                    minDateMessage={t("OC_NOT_SELECTABLE")}
                    onChange={handleLeaveStart}
                    onMonthChange={handleCurrentDate}
                    value={leaveStart}
                    format={getDateFormatByLocale(i18n.language)}
                    onFocus={() => {
                      setLeaveStartError(false);
                    }}
                    contractCalendar={contractCalendar}
                  />
                  <OcDatePicker
                    error={leaveEndError}
                    helperText={leaveEndError && t("OC_NOT_SELECTABLE")}
                    id="leaveEnd"
                    label={isAssigner ? t("OC_END_TIME") : t("MY_LEAVE_LEAVE_END")}
                    minDate={new Date("2020-09-01")}
                    minDateMessage={t("OC_NOT_SELECTABLE")}
                    onChange={handleLeaveEnd}
                    value={leaveEnd}
                    format={getDateFormatByLocale(i18n.language)}
                    onFocus={() => {
                      setLeaveEndError(false);
                    }}
                    contractCalendar={contractCalendar}
                  />
                </div>
                {isMobile && <Box width="26px" />}
                <OcTimeDateCounter
                  leaveStart={leaveStart}
                  leaveEnd={leaveEnd}
                  time={workDays}
                  timeloading={workDaysloading}
                  cssClasses="leave"
                  counterLabel={t("MY_LEAVE_COUNT_OF_WORKDAYS")}
                  isMobile={isMobile}
                />
              </>
            )}
          </div>
          {isMonthSplit && (
            <Box
              style={{
                width: isMobile ? "auto" : 400,
                textAlign: "center",
                marginTop: "8px",
                marginBottom: "8px",
              }}
            >
              <Typography
                variant="body2"
              >
                {t("MY_LEAVE_MONTH_SPLIT_INFO")}
              </Typography>
            </Box>
          )}
          <div style={{ marginTop: 28, marginBottom: 16 }}>
            {workDays !== effectiveWorkDays && leaveTypelist === true && (
              <>
                <MyLeaveEffectiveWorkDaysBlock
                  effectiveWorkDays={effectiveWorkDays}
                  effectiveWorkDaysByDays={effectiveWorkDaysByDays}
                  currentYearEffectiveWorkDays={currentYearEffectiveWorkDays}
                  nextYearEffectiveWorkDays={nextYearEffectiveWorkDays}
                />
                <Box height="32px" />
              </>
            )}
            <TextField
              id="message"
              label={t("OC_MESSAGE")}
              multiline
              value={message}
              onChange={handleMessage}
              fullWidth
              variant="outlined"
              helperText={
                message.length === 0
                  ? t("OC_OPTIONAL")
                  : `${message.length}/255`
              }
              style={{
                height: "auto",
              }}
              inputProps={{ maxLength: 255 }}
              FormHelperTextProps={
                message.length === 0
                  ? { style: { textAlign: "left" } }
                  : { style: { textAlign: "right" } }
              }
            />
          </div>
          {(coverageError ||
            workDaysError ||
            moreDaysError ||
            expiredContractError ||
            nextYearBlockedError ||
            invalidYearError ||
            weekDaysError) && (
              <Box
                style={{
                  display: "inline-box",
                  minHeight: "20px",
                  color: "#cc0033",
                  textAlign: "center",
                  width: isMobile ? "auto" : 400,
                  padding:
                    coverageError ||
                      workDaysError ||
                      moreDaysError ||
                      expiredContractError ||
                      nextYearBlockedError ||
                      invalidYearError ||
                      weekDaysError
                      ? "26px 0px 36px 0px"
                      : "0px 0px 20px 0px",
                }}
              >
                {weekDaysError && <div>{t("MY_LEAVE_SELECT_AT_LEAST_ONE_DAY")}</div>}
                {coverageError && <CoverageErrorMessage coverageErrorRequests={coverageErrorRequests} />}
                {workDaysError && <div>{t("MY_LEAVE_WORKDAYS_ERROR")}</div>}
                {moreDaysError && <div>{t("MY_LEAVE_MORE_DAYS_ERROR")}</div>}
                {expiredContractError && (
                  <div>{t("MY_LEAVE_EXPIRED_CONTRACT_ERROR")}</div>
                )}
                {nextYearBlockedError && (
                  <div>{t("MY_LEAVE_NEXT_YEAR_BLOCKED_ERROR")}</div>
                )}
                {invalidYearError && (
                  <div>{t("MY_LEAVE_INVALID_YEAR_ERROR")}</div>
                )}
              </Box>
            )}
          <div className={classes.line} />
          <Button
            fullWidth
            id="handleSave"
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={saveButtonDisabled || nextYearBlockedError || invalidYearError}
          >
            {t("OC_SAVE")}
          </Button>
          <div className={classes.divider} />
          <Button
            id="myleave-new-request-dialog-cancel-button"
            text={t("OC_CANCEL")}
            fullWidth
            color="secondary"
            variant="text"
            onClick={handleCancel}
          >
            {t("OC_CANCEL")}
          </Button>
          <div className={classes.divider} />
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MyLeaveNewRequestDialog;
